import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { StatusCode } from "api/protocols";
import {
  GetAccountBatchJobs,
  PostAccountBatchCancelSchedule,
  PostAccountBatchNow,
  TAccountBatchJob,
  TAccountBatchJobResult,
} from "api/rpc/2024-04/facilityAdmin/client/accounts/accounts";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { Badge, TBadgeType } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import Search from "components/search/Search";
import { ButtonNew as Button } from "components/buttonNew";
import { LocaleCurrency } from "helpers/Locale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import AccountBatchJobsAppliedFilters, {
  AccountBatchJobsAppliedFilterKey,
} from "./AccountBatchJobsAppliedFilters";
import AccountBatchJobsFilterDrawer, {
  AccountBatchJobsDrawerFilters,
  countActiveAccountBatchJobsFilters,
  emptyAccountBatchJobsDrawerFilters,
} from "./AccountBatchJobsFilterDrawer";
import "./accounts.scss";
import "./accountsFilterDrawer.scss";

const TABLE_LIMIT = 50;

function statusBadgeType(status: string): TBadgeType {
  switch (status) {
    case "complete":
    case "success":
      return "success";
    case "partial":
      return "warning";
    case "processing":
    case "pending":
    case "scheduled":
      return "grayblue";
    case "draft":
      return "warning";
    case "skipped":
    case "failed":
      return "error";
    default:
      return "gray";
  }
}

function displayStatus(status: string): string {
  if (status === "skipped") {
    return "Failed";
  }
  if (status === "partial") {
    return "Partial";
  }
  if (!status) {
    return "";
  }
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function typeLabel(type: string, t: (key: string) => string): string {
  if (type === "payment") {
    return t("Charge Payment");
  }
  if (type === "order") {
    return t("Sell Products");
  }
  return type;
}

type Props = {
  refreshKey?: number;
  onLoadSelection?: (job: TAccountBatchJob) => void;
  onChanged?: () => void;
};

export default function AccountBatchJobsTab(props: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const [jobs, setJobs] = useState<TAccountBatchJob[]>(undefined);
  const [offset, setOffset] = useState(0);
  const [filterDate, setFilterDate] = useState<Date>(new Date());
  const [useDateFilter, setUseDateFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [jobStatusFilter, setJobStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [expandedJobIds, setExpandedJobIds] = useState<number[]>([]);
  const [actionBusyId, setActionBusyId] = useState<number | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const drawerFilters: AccountBatchJobsDrawerFilters = useMemo(
    () => ({
      filterDate,
      useDateFilter,
      jobStatusFilter,
      statusFilter,
    }),
    [filterDate, useDateFilter, jobStatusFilter, statusFilter],
  );
  const activeDrawerFilterCount = countActiveAccountBatchJobsFilters(drawerFilters);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadJobs(source.token);
    return () => source.cancel();
  }, [offset, filterDate, useDateFilter, statusFilter, jobStatusFilter, search, props.refreshKey]);

  async function loadJobs(token?: CancelToken) {
    if (jobs !== undefined) {
      setJobs(undefined);
    }

    const res = await GetAccountBatchJobs(
      {
        date: useDateFilter ? moment(filterDate).format("YYYY-MM-DD") : undefined,
        status: statusFilter || undefined,
        job_status: jobStatusFilter || undefined,
        search: search.length > 2 ? search : undefined,
        limit: TABLE_LIMIT,
        offset,
      },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading batch jobs")));
      setJobs([]);
      return;
    }

    const nextJobs = res.data ?? [];
    setJobs(nextJobs);

    if (search.length > 2 || statusFilter || jobStatusFilter) {
      setExpandedJobIds(nextJobs.map(job => job.id));
    }
  }

  function toggleExpanded(jobId: number) {
    setExpandedJobIds(prev =>
      prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId],
    );
  }

  function openAccount(accountId: number) {
    history.push({
      pathname: "/admin/settings/accounts/overview/" + String(accountId),
      search: location.search,
    });
  }

  async function handlePostNow(job: TAccountBatchJob, event: React.MouseEvent) {
    event.stopPropagation();
    setActionBusyId(job.id);
    const res = await PostAccountBatchNow({ id: job.id }, true);
    setActionBusyId(null);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.message || t("Error posting batch")));
      return;
    }

    dispatch(showSuccess(t("Batch queued")));
    props.onChanged?.();
    void loadJobs();
  }

  async function handleCancelSchedule(job: TAccountBatchJob, event: React.MouseEvent) {
    event.stopPropagation();
    setActionBusyId(job.id);
    const res = await PostAccountBatchCancelSchedule({ id: job.id }, true);
    setActionBusyId(null);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.message || t("Error cancelling schedule")));
      return;
    }

    dispatch(showSuccess(t("Schedule cancelled")));
    props.onChanged?.();
    void loadJobs();
  }

  function handleLoadSelection(job: TAccountBatchJob, event: React.MouseEvent) {
    event.stopPropagation();
    props.onLoadSelection?.(job);
  }

  function applyDrawerFilters(next: AccountBatchJobsDrawerFilters) {
    setOffset(0);
    setFilterDate(next.filterDate);
    setUseDateFilter(next.useDateFilter);
    setJobStatusFilter(next.jobStatusFilter);
    setStatusFilter(next.statusFilter);
  }

  function clearFilters() {
    const empty = emptyAccountBatchJobsDrawerFilters();
    setOffset(0);
    setFilterDate(empty.filterDate);
    setUseDateFilter(empty.useDateFilter);
    setJobStatusFilter(empty.jobStatusFilter);
    setStatusFilter(empty.statusFilter);
  }

  function removeAppliedFilter(key: AccountBatchJobsAppliedFilterKey) {
    setOffset(0);
    switch (key) {
      case "date":
        setUseDateFilter(false);
        break;
      case "job_status":
        setJobStatusFilter("");
        break;
      case "status":
        setStatusFilter("");
        break;
      default:
        break;
    }
  }

  function renderAccountRows(job: TAccountBatchJob, results: TAccountBatchJobResult[]) {
    if (!results.length) {
      return (
        <tr key={`${job.id}-empty`}>
          <td className="batch-job-expand-cell" />
          <td colSpan={7}>
            <p className="table-cell-support-text pl-4">{t("No accounts in this batch")}</p>
          </td>
        </tr>
      );
    }

    return results.map(result => (
      <tr
        key={`${job.id}-${result.account_id}-${result.status}-${result.message}`}
        className="clickable"
        onClick={() => openAccount(result.account_id)}
      >
        <td className="batch-job-expand-cell" />
        <td colSpan={2} className="batch-job-account-cell">
          <div className="batch-job-account-identity">
            <p className="table-cell-lead-text">{result.customer_name || "—"}</p>
            <p className="table-cell-support-text">
              {result.account_title || `#${result.account_id}`}
              {result.account_number ? ` · ${result.account_number}` : ""}
            </p>
          </div>
        </td>
        <td>
          <Badge type={statusBadgeType(result.status)} size="medium">
            {displayStatus(result.status)}
          </Badge>
        </td>
        <td>
          {typeof result.amount === "number" ? <LocaleCurrency amount={result.amount} /> : "—"}
        </td>
        <td colSpan={2} className="batch-job-message-cell">
          {result.message}
        </td>
        <td>
          {result.order_id ? (
            <button
              type="button"
              className="table-cell-lead-text"
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
              onClick={event => {
                event.stopPropagation();
                window.open("/admin/order/" + String(result.order_id), "_blank", "noopener,noreferrer");
              }}
            >
              {t("Order")} #{result.order_id}
            </button>
          ) : (
            "—"
          )}
        </td>
      </tr>
    ));
  }

  return (
    <div className="account-batch-jobs-tab">
      <AccountBatchJobsAppliedFilters
        filters={drawerFilters}
        onRemoveFilter={removeAppliedFilter}
        onClearAll={clearFilters}
      />
      <div className="accounts-search-row flex flex-row flex-nowrap items-center gap-3 w-full mb-4">
        <div className="accounts-search flex-1 min-w-0">
          <Search
            historyKey="account_batch_jobs"
            searchCallback={value => {
              setOffset(0);
              setSearch(value);
            }}
            placeholder={t("Search customers or accounts...")}
          />
        </div>
        <Button
          type="secondary"
          size="medium"
          className="accounts-filter-button flex-shrink-0"
          onClick={() => setFilterDrawerOpen(true)}
        >
          <FontAwesomeIcon icon={faBarsFilter} className="mr-2" />
          {activeDrawerFilterCount > 0
            ? t("Filter ({{count}})", { count: activeDrawerFilterCount })
            : t("Filter")}
        </Button>
      </div>

      <AccountBatchJobsFilterDrawer
        open={filterDrawerOpen}
        appliedFilters={drawerFilters}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={applyDrawerFilters}
      />

      <div className="account-batch-jobs-table-scroll">
        <DataTable
          className="table-account-batch-jobs"
          textWrap
          columns={[
            { label: "", width: "40px" },
            { label: t("Batch"), width: "240px" },
            { label: t("Type"), width: "140px" },
            { label: t("Status"), width: "110px" },
            { label: t("Accounts"), width: "90px" },
            { label: t("Succeeded"), width: "100px" },
            { label: t("Failed"), width: "90px" },
            { label: t("Created"), width: "170px" },
          ]}
          loading={jobs === undefined}
          footer={{
            tableLimit: TABLE_LIMIT,
            tableOffset: offset,
            handleTableOffset: direction => {
              setOffset(prev => (direction === "next" ? prev + TABLE_LIMIT : Math.max(0, prev - TABLE_LIMIT)));
            },
            disableNextOffset: !jobs || !(jobs?.length === TABLE_LIMIT),
          }}
        >
          {jobs?.map(job => {
            const expanded = expandedJobIds.includes(job.id);
            const editable = job.status === "draft" || job.status === "scheduled";
            return (
              <React.Fragment key={job.id}>
                <tr className="clickable batch-job-primary-row" onClick={() => toggleExpanded(job.id)}>
                  <td className="batch-job-expand-cell">
                    <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                  </td>
                  <td className="batch-job-batch-cell">
                    <div className="batch-job-batch-row">
                      <p className="table-cell-lead-text batch-job-batch-title">
                        #{job.id}{job.title ? ` · ${job.title}` : ""}
                      </p>
                      {editable ? (
                        <div className="batch-job-actions" onClick={event => event.stopPropagation()}>
                          <Button
                            type="text"
                            size="small"
                            disabled={actionBusyId === job.id}
                            onClick={event => handlePostNow(job, event)}
                          >
                            {t("Post now")}
                          </Button>
                          {job.status === "scheduled" ? (
                            <Button
                              type="text"
                              size="small"
                              disabled={actionBusyId === job.id}
                              onClick={event => handleCancelSchedule(job, event)}
                            >
                              {t("Cancel schedule")}
                            </Button>
                          ) : null}
                          <Button type="text" size="small" onClick={event => handleLoadSelection(job, event)}>
                            {t("Load selection")}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                    {job.status === "scheduled" && job.scheduled_at ? (
                      <p className="table-cell-support-text">
                        {t("Scheduled")}: {moment(job.scheduled_at).format("MMM D, YYYY h:mm A")}
                      </p>
                    ) : null}
                  </td>
                  <td className="batch-job-type-cell">{typeLabel(job.type, t)}</td>
                  <td>
                    <Badge type={statusBadgeType(job.status)} size="medium">
                      {displayStatus(job.status)}
                    </Badge>
                  </td>
                  <td className="batch-job-numeric-cell">{job.account_count ?? 0}</td>
                  <td className="batch-job-numeric-cell">{job.success_count ?? 0}</td>
                  <td className="batch-job-numeric-cell">
                    {(job.failed_count ?? 0) + (job.skipped_count ?? 0)}
                  </td>
                  <td className="batch-job-created-cell">
                    {job.created_at ? moment(job.created_at).format("MMM D, YYYY h:mm A") : "—"}
                  </td>
                </tr>
                {expanded ? renderAccountRows(job, job.results ?? []) : null}
              </React.Fragment>
            );
          })}
        </DataTable>
      </div>
    </div>
  );
}
